import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, BellOff, BellRing, Users, User, AlertTriangle, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api, { notifyAPI, vehicleBroadcastAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { RecipientListEditor } from '../../components/shared/RecipientListEditor';

// ONE view of who actually gets contacted for a vehicle. Reads the combined
// state of BOTH notification systems from /command-center/vehicle-notifications
// and writes through their EXISTING endpoints, so Used Vehicles and this screen
// can never hold different data.
export default function VehicleNotificationsPanel({ organizationId }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [people, setPeople] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [edit, setEdit] = useState(null);
    const [phonesOk, setPhonesOk] = useState(true);

    const load = useCallback(async () => {
        if (!organizationId) return;
        try {
            const [ov, pp] = await Promise.all([
                api.get('/command-center/vehicle-notifications', { params: { organization_id: organizationId } }),
                notifyAPI.people(organizationId),
            ]);
            setData(ov.data);
            setPeople(pp.data?.people || []);
        } catch { /* keep last */ } finally { setLoading(false); }
    }, [organizationId]);

    useEffect(() => { load(); }, [load]);

    const assign = async (vehicleId, personId) => {
        setBusy(true);
        try {
            await notifyAPI.setRouting('vehicle', {
                organization_id: organizationId, record_ids: [vehicleId],
                notify_employee_id: personId || null,
                notify_target: personId ? 'assignee' : 'none',
            });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('common.error', 'Something went wrong'));
        } finally { setBusy(false); }
    };

    const saveBroadcast = async () => {
        setBusy(true);
        try {
            // SAME helper Used Vehicles saves through (PUT /vehicles/{id}/broadcast)
            // — one store, so the two screens cannot disagree.
            await vehicleBroadcastAPI.update(edit.vehicle_id, {
                notification_phones: edit.uses_default ? null : (edit.rows || []).filter((r) => (r.phone || '').trim()),
                use_default: edit.uses_default, muted: edit.muted,
            });
            toast.success(t('vehNotify.saved', 'Notification settings saved'));
            setEdit(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('common.error', 'Something went wrong'));
        } finally { setBusy(false); }
    };

    if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-amber-400" /></div>;
    if (!data) return null;

    const srcLabel = (b) => (b.muted
        ? t('vehNotify.srcMuted', 'Muted')
        : b.uses_default
            ? t('vehNotify.srcDefault', 'Dealership default list')
            : t('vehNotify.srcVehicle', 'This vehicle only'));

    return (
        <div className="space-y-4" data-testid="cc-vehicle-notifications">
            {data.demo_suppressed && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3"
                    data-testid="cc-vn-demo-banner">
                    <BellOff className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                        {t('vehNotify.demoOverride', 'Demo Notifications is OFF for this organization, so nothing below actually sends a text. Everything is logged only.')}
                    </p>
                </div>
            )}

            {data.live_texts_forced && (
                <div className="flex items-start gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3"
                    data-testid="cc-vn-live-banner">
                    <BellRing className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-emerald-700 dark:text-emerald-300">
                        <span className="font-bold uppercase tracking-wide">
                            {t('vehNotify.liveTextsOn', 'Live texts on')}
                        </span>
                        {' — '}
                        {t('vehNotify.liveTextsHint', 'Alerts from this organization are sent for real and billed, even though it is a demo account.')}
                    </p>
                </div>
            )}

            <p className="text-sm text-muted-foreground" data-testid="cc-vn-summary">
                {t('vehNotify.summary', '{{total}} vehicles · {{nobody}} would notify nobody', {
                    total: data.totals.vehicles, nobody: data.totals.nobody_notified,
                })}
            </p>

            <div className="space-y-2">
                {data.vehicles.map((v) => (
                    <div key={v.vehicle_id}
                        className="rounded-lg border border-border p-3 space-y-2"
                        data-testid={`cc-vn-row-${v.stock_number || v.vehicle_id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="min-w-0">
                                <p className="text-sm font-medium truncate text-foreground">
                                    <span>{v.label || t('vehNotify.unnamed', 'Vehicle')}</span>
                                    <span className="text-muted-foreground"> · #{v.stock_number || '—'}</span>
                                </p>
                            </div>
                            {v.nobody_notified ? (
                                <Badge variant="outline" className="border-red-500/50 text-red-500 gap-1"
                                    data-testid={`cc-vn-nobody-${v.stock_number || v.vehicle_id}`}>
                                    <AlertTriangle className="w-3 h-3" />
                                    {t('vehNotify.nobody', 'Nobody is notified')}
                                </Badge>
                            ) : (
                                <Badge variant="outline" className="border-emerald-500/50 text-emerald-600 dark:text-emerald-400">
                                    {t('vehNotify.willNotify', '{{count}} will be texted', { count: v.effective_count })}
                                </Badge>
                            )}
                        </div>

                        <div className="grid gap-2 sm:grid-cols-2">
                            <div className="flex items-center gap-2">
                                <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                <span className="text-xs text-muted-foreground shrink-0">
                                    {t('vehNotify.assigned', 'Assigned')}
                                </span>
                                <select
                                    className="flex-1 min-w-0 rounded-md border border-border bg-background px-2 py-1 text-sm"
                                    value={v.assigned.employee_id || ''}
                                    disabled={busy}
                                    onChange={(e) => assign(v.vehicle_id, e.target.value)}
                                    data-testid={`cc-vn-assign-${v.stock_number || v.vehicle_id}`}
                                >
                                    <option value="">{t('vehNotify.nobodyAssigned', 'Nobody')}</option>
                                    {people.map((p) => (
                                        <option key={p.id} value={p.id}>{p.name}{p.phone ? '' : ` — ${t('vehNotify.noPhone', 'no phone')}`}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <Users className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                <span className="text-xs text-muted-foreground shrink-0">{srcLabel(v.broadcast)}</span>
                                <span className="text-sm truncate flex-1 min-w-0">
                                    {v.broadcast.recipients.length
                                        ? v.broadcast.recipients.map((r) => r.name || r.phone).join(', ')
                                        : '—'}
                                </span>
                                <Button variant="ghost" size="sm" disabled={busy}
                                    onClick={() => { setPhonesOk(true); setEdit({
                                        vehicle_id: v.vehicle_id, stock_number: v.stock_number, label: v.label,
                                        muted: v.broadcast.muted, uses_default: v.broadcast.uses_default,
                                        rows: v.broadcast.recipients.map((r) => ({ name: r.name || '', phone: r.phone })),
                                    }); }}
                                    data-testid={`cc-vn-edit-${v.stock_number || v.vehicle_id}`}>
                                    <Pencil className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                        </div>

                        {v.assigned.unreachable && (
                            <p className="text-xs text-red-500" data-testid={`cc-vn-unreachable-${v.stock_number || v.vehicle_id}`}>
                                {t('vehNotify.assignedNoPhone', 'The assigned person has no phone number, so they cannot be texted.')}
                            </p>
                        )}
                    </div>
                ))}
                {data.vehicles.length === 0 && (
                    <p className="text-sm text-muted-foreground" data-testid="cc-vn-empty">
                        {t('vehNotify.noVehicles', 'No vehicles in this organization yet.')}
                    </p>
                )}
            </div>

            <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
                <DialogContent data-testid="cc-vn-modal">
                    <DialogHeader>
                        <DialogTitle className="text-base">{t('vehNotify.editTitle', 'Broadcast recipients')}</DialogTitle>
                        <DialogDescription>{edit?.label} · #{edit?.stock_number}</DialogDescription>
                    </DialogHeader>
                    {edit && (
                        <div className="space-y-3">
                            <label className="flex items-center gap-2 text-sm">
                                <Checkbox checked={edit.muted}
                                    onCheckedChange={(c) => setEdit({ ...edit, muted: !!c })}
                                    data-testid="cc-vn-mute" />
                                {t('vehNotify.mute', 'Mute tap and unclaimed alerts for this vehicle')}
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <Checkbox checked={edit.uses_default} disabled={edit.muted}
                                    onCheckedChange={(c) => setEdit({ ...edit, uses_default: !!c })}
                                    data-testid="cc-vn-use-default" />
                                {t('vehNotify.useDefault', 'Use the dealership default list')}
                            </label>
                            {!edit.uses_default && (
                                <RecipientListEditor
                                    value={edit.rows}
                                    onChange={(rows) => setEdit({ ...edit, rows })}
                                    onValidityChange={setPhonesOk}
                                    disabled={edit.muted}
                                    testid="cc-vn-recipients"
                                />
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEdit(null)} data-testid="cc-vn-cancel">
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button onClick={saveBroadcast} disabled={busy || !phonesOk} data-testid="cc-vn-save">
                            {t('common.save', 'Save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
